import React, { useState, useMemo } from 'react';
import { Box, Menu, MenuItem, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import useUserAuth from '../../../../utils/useUserAuth';
import {
  TicketCard,
  ExpiredCardWrapper,
  CardInfoSection,
  HeaderRow,
  HeaderLeft,
  ClassTypeLabel,
  TicketTitle,
  TypeLabel,
  InfoRow,
  InfoLeft,
  InfoLabel,
  InfoValue,
  HighlightText,
  ExpiredLabel,
  UpcomingLabel,
} from './CourseTicketCardStyled';
import { CardButtonArea, CardButton } from './PassCardStyled';

const URGENT_DAYS_THRESHOLD = 10;

/**
 * 회원상세 라커 카드 — 라커 1개 = 카드 1장.
 * 이용권이 1개인 자리와 미배정 패스는 다른 수강권/이용권 카드와 같은 레이아웃
 * (헤더 + 유효기간 행 + 하단 버튼 영역)을 쓰고, 이용권이 여러 개인 자리만
 * 카드 내부 이용권 스택(라디오 단일 선택)으로 펼친다.
 * 액션은 항상 선택된 이용권에 적용된다.
 */
// 기존 카드 팔레트와 동일 톤 — 이용중 #4AB3BC(HighlightText), 사용예정 #55C982(UpcomingLabel),
// 중지 #EE735A, 만료 #737373(ExpiredLabel)
const STATUS_BADGES = {
  current: { text: '이용중', color: '#4AB3BC', bg: 'rgba(74,179,188,0.12)' },
  upcoming: { text: '사용예정', color: '#55C982', bg: 'rgba(85,201,130,0.12)' },
  stopped: { text: '중지', color: '#EE735A', bg: 'rgba(238,115,90,0.12)' },
  expired: { text: '만료', color: '#737373', bg: '#F0F0F0' },
};

const passKind = item => {
  if (item.status === 'STOPPED') return 'stopped';
  const today = dayjs().startOf('day');
  if (item.status !== 'ACTIVE' || dayjs(item.expireDate).isBefore(today, 'day')) return 'expired';
  if (dayjs(item.startDate).isAfter(today, 'day')) return 'upcoming';
  return 'current';
};

// 스택 행은 배지·기간·잔여를 한 줄에 담아야 해서 연도를 두 자리로 줄인다 (26.08.04).
// 네 자리로 두면 잔여 일수와 부딪혀 줄이 깨진다.
const fullPeriod = item =>
  `${dayjs(item.startDate).format('YY.MM.DD')} ~ ${dayjs(item.expireDate).format('YY.MM.DD')}`;

// 스택 행 오른쪽 잔여 표기. 만료는 배지에 이미 드러나므로 생략한다.
const remainingLabel = item => {
  const kind = passKind(item);
  if (kind === 'expired') return null;
  const today = dayjs().startOf('day');
  if (kind === 'upcoming') {
    const days = dayjs(item.startDate).startOf('day').diff(today, 'day');
    return { text: `${days}일 후 시작`, urgent: false };
  }
  if (!item.expireDate) return null;
  const days = dayjs(item.expireDate).startOf('day').diff(today, 'day');
  return {
    text: days === 0 ? '오늘 종료' : `${days}일 남음`,
    urgent: days <= URGENT_DAYS_THRESHOLD,
  };
};

const LockerSpotCard = ({ group, isExpired, onAssign, onMove, onUnassign, onPeriodChange, onStop, onExtend }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canProductPeriodChange = useUserAuth('MEMBER_PRODUCT_PERIOD_CHANGE');
  const [menuAnchor, setMenuAnchor] = useState(null);

  const sorted = useMemo(
    () => [...group].sort((a, b) => (a.startDate || '').localeCompare(b.startDate || '')),
    [group]
  );
  const representative = useMemo(() => sorted.find(item => item.primary) ?? sorted[0], [sorted]);
  const [selectedId, setSelectedId] = useState(representative.memberLockerPassId);
  const selected = sorted.find(item => item.memberLockerPassId === selectedId) ?? representative;

  const isUnassigned = !selected.branchLockerId;
  const multi = sorted.length > 1;

  // 라커 칩 라벨 — 영역 이름과 번호를 함께 (예: "남자 라커 7번 ↗")
  const spotLabel = `${selected.lockerAreaName ? `${selected.lockerAreaName} ` : ''}${selected.lockerNumber}${t('번')}`;

  // 선택된 이용권 기준 D-day (다른 카드들과 동일 규칙)
  const today = dayjs().startOf('day');
  const kind = passKind(selected);
  const isUpcoming = kind === 'upcoming';
  const daysRemaining =
    !isUpcoming && selected.expireDate ? dayjs(selected.expireDate).startOf('day').diff(today, 'day') : null;
  const isUrgent = daysRemaining !== null && daysRemaining >= 0 && daysRemaining <= URGENT_DAYS_THRESHOLD;
  // 복수 스택에서 만료 패스를 '선택'했다고 카드 전체를 회색으로 감싸지 않는다.
  // 회색(만료) 처리는 단일 이용권 카드에만 적용.
  const cardExpired = isExpired || (!multi && kind === 'expired');

  const openLockerPage = () => {
    const params = new URLSearchParams({ lockerNumber: selected.lockerNumber });
    if (selected.lockerAreaName) params.set('lockerAreaName', selected.lockerAreaName);
    navigate(`/locker?${params.toString()}`);
  };

  // ⋯ 메뉴 — 배정 카드: 자리 변경/배정 해제(+ 단일 카드는 연장 추가), 미배정: 중지 설정.
  // 만료는 상태가 아직 ACTIVE 여도(스케줄러 전) 중지할 수 없다 — 기간 변경만 남긴다.
  const menuItems = (isUnassigned
    ? [
        kind !== 'expired' && { label: t('라커 중지 설정'), onClick: () => onStop(selected) },
      ]
    : [
        { label: t('자리 변경'), onClick: () => onMove(selected) },
        { label: t('배정 해제'), onClick: () => onUnassign(selected) },
        !multi && onExtend && { label: t('연장 추가'), onClick: () => onExtend(selected) },
      ]
  ).filter(Boolean);

  // 우측 잔여 표시 (InfoRow) — ProductCard 와 동일 규칙
  const remainNode = cardExpired ? (
    <ExpiredLabel>{t('만료')}</ExpiredLabel>
  ) : isUpcoming ? (
    <UpcomingLabel>{t('사용예정')}</UpcomingLabel>
  ) : (
    daysRemaining !== null &&
    daysRemaining >= 0 && (
      <HighlightText urgent={isUrgent}>
        {daysRemaining}
        {t('일 남음')}
      </HighlightText>
    )
  );

  const headerChip = isUnassigned ? (
    <ClassTypeLabel sx={{ fontWeight: 700, color: '#5B6470' }}>{t('미배정')}</ClassTypeLabel>
  ) : (
    <ClassTypeLabel
      clickable
      onClick={openLockerPage}
      sx={{ fontSize: '13px', fontWeight: 800, color: '#0F7F85' }}>
      {spotLabel} ↗
    </ClassTypeLabel>
  );

  const periodChangeButton = (
    <Tooltip title={!canProductPeriodChange ? t('권한이 없습니다') : ''} arrow>
      <CardButton
        variant="text"
        onClick={canProductPeriodChange ? () => onPeriodChange(selected) : undefined}
        style={!canProductPeriodChange ? { opacity: 0.5, cursor: 'default' } : {}}>
        {t('기간 변경')}
      </CardButton>
    </Tooltip>
  );

  const moreButton = menuItems.length > 0 && (
    <CardButton variant="text" noBorderLeft onClick={e => setMenuAnchor(e.currentTarget)}>
      ⋯
    </CardButton>
  );

  const card = (
    <TicketCard>
      <CardInfoSection>
        <HeaderRow>
          <HeaderLeft>
            {headerChip}
            <TicketTitle>{selected.productName || t('라커 이용권')}</TicketTitle>
          </HeaderLeft>
          <TypeLabel sx={kind === 'stopped' ? { color: '#EE735A' } : {}}>
            {kind === 'stopped' ? t('중지') : t('락커')}
          </TypeLabel>
        </HeaderRow>

        {multi ? (
          <>
            {/* 이용권 스택 — 라디오로 1개만 선택, 액션은 선택된 이용권에 적용 */}
            <Box sx={{ border: '1px solid #d9d9d9', background: '#fff', borderRadius: '8px', overflow: 'hidden' }}>
              {sorted.map((item, idx) => {
                const badge = STATUS_BADGES[passKind(item)];
                const remaining = remainingLabel(item);
                const isSel = item.memberLockerPassId === selected.memberLockerPassId;
                return (
                  <Box
                    key={item.memberLockerPassId}
                    onClick={() => setSelectedId(item.memberLockerPassId)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: '10px', p: '10px 12px',
                      borderTop: idx > 0 ? '1px solid #ececec' : 'none',
                      background: isSel ? 'rgba(74,179,188,0.10)' : 'transparent',
                      cursor: 'pointer',
                      '&:hover': !isSel ? { background: '#F2F7F8' } : undefined,
                    }}>
                    <Box
                      component="span"
                      sx={{
                        width: 16, height: 16, borderRadius: '50%', boxSizing: 'border-box', flexShrink: 0,
                        border: isSel ? '5px solid #4AB3BC' : '1.5px solid #c4c4c4',
                        background: '#fff',
                      }}
                    />
                    {/* 배지 크기를 통일해 '만료'·'사용예정' 이 같은 폭으로 보이게 한다 */}
                    <Box
                      component="span"
                      sx={{
                        width: 56, boxSizing: 'border-box', textAlign: 'center', flexShrink: 0,
                        fontSize: 11, fontWeight: 700, color: badge.color, background: badge.bg,
                        borderRadius: '5px', p: '2px 0',
                      }}>
                      {t(badge.text)}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        flex: 1, minWidth: 0, whiteSpace: 'nowrap', fontSize: 13,
                        fontWeight: isSel ? 700 : 500,
                        color: isSel ? '#262626' : '#555', letterSpacing: '-0.12px',
                      }}>
                      {fullPeriod(item)}
                    </Box>
                    {remaining && (
                      <Box
                        component="span"
                        sx={{
                          flexShrink: 0, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                          color: remaining.urgent ? '#EE735A' : '#8C8C8C',
                        }}>
                        {t(remaining.text)}
                      </Box>
                    )}
                  </Box>
                );
              })}
              {onExtend && !cardExpired && (
                <Box
                  onClick={() => onExtend(selected)}
                  sx={{
                    p: '9px 12px', borderTop: '1px dashed #d9d9d9', textAlign: 'center',
                    fontSize: 12.5, fontWeight: 600, color: '#4AB3BC', cursor: 'pointer',
                    '&:hover': { background: 'rgba(74,179,188,0.06)' },
                  }}>
                  + {t('연장 추가')}
                </Box>
              )}
            </Box>
          </>
        ) : (
          <InfoRow>
            <InfoLeft>
              <InfoLabel>{t('유효기간')}</InfoLabel>
              <InfoValue>
                {selected.startDate?.replaceAll('-', '.')} ~ {selected.expireDate?.replaceAll('-', '.')}
              </InfoValue>
            </InfoLeft>
            {remainNode}
          </InfoRow>
        )}
      </CardInfoSection>

      {/* 하단 버튼 영역 — 다른 카드들과 같은 톤 */}
      <CardButtonArea>
        {isUnassigned ? (
          <>
            {/* 만료된 미배정 이용권은 배정할 자리가 없다 — 기간 변경만 남긴다 */}
            {kind !== 'expired' && (
              <CardButton variant="text" onClick={() => onAssign(selected)}>
                {t('라커 배정')}
              </CardButton>
            )}
            {periodChangeButton}
            {moreButton}
          </>
        ) : (
          <>
            {periodChangeButton}
            {kind !== 'expired' && (
              <CardButton variant="text" noBorderLeft disabled={!selected.stopTargetPassId} onClick={() => onStop(selected)}>
                {t('중지 설정')}
              </CardButton>
            )}
            {moreButton}
          </>
        )}
      </CardButtonArea>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        {menuItems.map(mi => (
          <MenuItem
            key={mi.label}
            disabled={mi.disabled}
            onClick={() => {
              setMenuAnchor(null);
              mi.onClick();
            }}>
            {mi.label}
          </MenuItem>
        ))}
      </Menu>
    </TicketCard>
  );

  // 옆 카드(복수 스택)가 길어도 같이 늘어나지 않게 자기 내용 높이만 갖는다
  if (cardExpired) {
    return <Box sx={{ alignSelf: 'flex-start' }}><ExpiredCardWrapper>{card}</ExpiredCardWrapper></Box>;
  }
  return <Box sx={{ alignSelf: 'flex-start' }}>{card}</Box>;
};

export default LockerSpotCard;
